import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";

// Minimal app-wide toast system. The app had no toast/notification primitive, so
// mutation flows reached for window.alert / window.confirm / inline text. This
// provides a single ToastProvider (mounted at the router root) and a useToast()
// hook returning a `notify` function. A toast may carry one optional action
// button — used to replace a blocking confirm with a non-blocking "do it" toast.

type ToastKind = "success" | "error" | "info";
export type ToastAction = { label: string; onClick: () => void };
export type ToastInput = {
  message: string;
  kind?: ToastKind;
  action?: ToastAction;
  // ms before auto-dismiss; 0 keeps it until dismissed. Defaults: 8s with an
  // action (time to read + act), 5s otherwise.
  duration?: number;
};
type Toast = ToastInput & { id: number; kind: ToastKind };

const ToastContext = createContext<((input: ToastInput) => void) | null>(null);

const STYLES: Record<ToastKind, string> = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-900",
  error: "border-red-200 bg-red-50 text-red-900",
  info: "border-gray-200 bg-white text-gray-800",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback(
    (input: ToastInput) => {
      const id = nextId.current++;
      const kind = input.kind ?? "info";
      setToasts((prev) => [...prev, { ...input, id, kind }]);
      const duration = input.duration ?? (input.action ? 8000 : 5000);
      if (duration > 0) setTimeout(() => dismiss(id), duration);
    },
    [dismiss]
  );

  return (
    <ToastContext.Provider value={notify}>
      {children}
      {/* z above the 90vh dialog (z-50) so toasts are never occluded. */}
      <div className="fixed bottom-4 right-4 z-[100] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm shadow-lg ${STYLES[toast.kind]}`}
          >
            <span className="flex-1">{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                onClick={() => {
                  toast.action?.onClick();
                  dismiss(toast.id);
                }}
                className="shrink-0 font-medium underline hover:no-underline"
              >
                {toast.action.label}
              </button>
            )}
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss"
              className="shrink-0 opacity-50 hover:opacity-100"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const notify = useContext(ToastContext);
  if (!notify) throw new Error("useToast must be used within a ToastProvider");
  return notify;
}
